import React, { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { HierarchicalTableData, HierarchicalNode } from '@/store/slices/opsChatSlice'
import { useLocalStorageSet } from '@/hooks/useLocalStorage'

interface HierarchicalTableProps {
  tableData: HierarchicalTableData
  className?: string
  disablePersistence?: boolean // Optional prop to disable persistence if needed
}

const HierarchicalTable: React.FC<HierarchicalTableProps> = ({ 
  tableData, 
  className = '',
  disablePersistence = false
}) => {
  // Generate a unique storage key based on table metadata
  const storageKey = useMemo(() => {
    if (disablePersistence) return ''
    
    // Create unique key from table metadata to ensure different tables have different expansion states
    const keyParts = [
      'hierarchical-table',
      tableData.title?.replace(/[^a-zA-Z0-9]/g, '-') || 'untitled',
      tableData.metadata?.store || 'all',
      tableData.metadata?.week || 'any',
      tableData.metadata?.year || 'any'
    ]
    return keyParts.join('-').toLowerCase()
  }, [tableData.title, tableData.metadata?.store, tableData.metadata?.week, tableData.metadata?.year, disablePersistence])

  // State to track which nodes are expanded
  // Use localStorage for persistence unless disabled
  const [localExpandedNodes, setLocalExpandedNodes] = useState<Set<string>>(new Set())
  const [persistedExpandedNodes, setPersistedExpandedNodes] = useLocalStorageSet(storageKey, new Set())
  
  // Choose which state to use based on persistence setting
  const [expandedNodes, setExpandedNodes] = disablePersistence 
    ? [localExpandedNodes, setLocalExpandedNodes]
    : [persistedExpandedNodes, setPersistedExpandedNodes]

  // Toggle expansion state for a node
  const toggleNode = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes)
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId)
    } else {
      newExpanded.add(nodeId)
    }
    setExpandedNodes(newExpanded)
  }

  // Get all metric column names - use backend-provided order if available
  const getAllMetricColumns = (): string[] => {
    // First try to use the ordered columns from backend metadata
    if (tableData.metadata?.orderedColumns && Array.isArray(tableData.metadata.orderedColumns)) {
      // Filter to only include columns that actually exist in the data
      const availableColumns = new Set<string>()
      const collectAvailableColumns = (node: HierarchicalNode) => {
        Object.keys(node.metrics).forEach(key => availableColumns.add(key))
        if (node.children) {
          node.children.forEach(child => collectAvailableColumns(child))
        }
      }
      collectAvailableColumns(tableData.data)
      
      return tableData.metadata.orderedColumns.filter((col: string) => availableColumns.has(col))
    }
    
    // Fallback: collect columns from data (preserve insertion order)
    const columnsOrder: string[] = []
    const seenColumns = new Set<string>()
    
    const collectColumns = (node: HierarchicalNode) => {
      Object.keys(node.metrics).forEach(key => {
        if (!seenColumns.has(key)) {
          columnsOrder.push(key)
          seenColumns.add(key)
        }
      })
      if (node.children) {
        node.children.forEach(child => collectColumns(child))
      }
    }
    
    collectColumns(tableData.data)
    return columnsOrder
  }

  const metricColumns = getAllMetricColumns()

  // Format value for display
  const formatValue = (value: any, columnName: string): string => {
    if (value === null || value === undefined || value === '' || value === 'BLANK()') return ''
    if (typeof value === 'number') {
      const isPercentage = columnName.includes('%') || columnName.includes('Percent') ||
                          columnName === 'Avg Discount' || columnName === 'Avg Discount LY' ||
                          columnName === 'Item Sell-Thru' || columnName === 'Item Sell-Thru LY' ||
                          columnName === '$ Sell-Thru' || columnName === '$ Sell-Thru LY'
      
      let formattedNumber: string
      if (Math.abs(value) >= 1000) {
        formattedNumber = value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
      } else {
        formattedNumber = value.toFixed(2)
      }
      
      return isPercentage ? `${formattedNumber}%` : formattedNumber
    }
    return String(value)
  }

  // Get indentation based on depth
  const getIndentationClass = (depth: number): string => {
    const baseIndent = 2
    const indentPerLevel = 4
    return `pl-${baseIndent + (depth * indentPerLevel)}`
  }

  // Get row styling based on type and depth
  const getRowStyling = (type: string, depth: number): string => {
    switch (type) {
      case 'store': 
        return 'bg-blue-50 dark:bg-blue-900/20 font-bold border-b-2 border-blue-200 dark:border-blue-700'
      case 'type': 
        return 'bg-gray-50 dark:bg-gray-800/50 font-semibold'
      case 'department': 
        return 'hover:bg-gray-50 dark:hover:bg-gray-800/30'
      default: 
        return ''
    }
  }

  // Render a single node and its children (recursively)
  const renderNode = (node: HierarchicalNode, depth: number = 0): React.ReactElement[] => {
    const isExpanded = expandedNodes.has(node.id)
    const hasChildren = node.children && node.children.length > 0
    const shouldShowChildren = isExpanded && hasChildren

    const nodeRow = (
      <tr 
        key={node.id}
        className={`border-b border-gray-200 dark:border-gray-700 transition-colors ${getRowStyling(node.type, depth)}`}
      >
        {/* Name column with expand/collapse and indentation */}
        <td className={`py-3 border-b border-gray-200 dark:border-gray-700 ${getIndentationClass(depth)}`}>
          <div className="flex items-center">
            {hasChildren ? (
              <button
                onClick={() => toggleNode(node.id)}
                className="mr-2 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            ) : (
              <div className="w-6 mr-2" />
            )}
            <span className={
              node.type === 'store' ? 'font-bold' : 
              node.type === 'type' ? 'font-semibold' : 
              ''
            }>
              {node.name}
            </span>
          </div>
        </td>

        {/* Metric columns */}
        {metricColumns.map((column) => {
          const value = node.metrics[column]
          return (
            <td 
              key={`${node.id}-${column}`}
              className="px-3 py-3 border-b border-gray-200 dark:border-gray-700 text-right tabular-nums"
            >
              {formatValue(value, column)}
            </td>
          )
        })}
      </tr>
    )

    // Return this node plus its children (if expanded)
    const result = [nodeRow]
    
    if (shouldShowChildren && node.children) {
      node.children.forEach(child => {
        result.push(...renderNode(child, depth + 1))
      })
    }

    return result
  }

  // Expand/Collapse all functionality
  const expandAll = () => {
    const allNodeIds = new Set<string>()
    
    const collectNodeIds = (node: HierarchicalNode) => {
      if (node.children && node.children.length > 0) {
        allNodeIds.add(node.id)
        node.children.forEach(child => collectNodeIds(child))
      }
    }
    
    collectNodeIds(tableData.data)
    setExpandedNodes(allNodeIds)
  }

  const collapseAll = () => {
    setExpandedNodes(new Set())
  }

  if (!tableData.data) {
    return (
      <div className={`bg-card rounded-lg border p-6 shadow-sm w-full max-w-full ${className}`}>
        <div className="text-center text-muted-foreground">No hierarchical data available</div>
      </div>
    )
  }

  return (
    <div className={`bg-card rounded-lg border p-6 shadow-sm w-full max-w-full ${className}`}>
      {/* Table metadata */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground">{tableData.title}</h3>
        {tableData.metadata && (
          <div className="text-xs text-muted-foreground mt-1">
            Generated at {new Date(tableData.metadata.generatedAt).toLocaleString()}
            {tableData.metadata.source && ` • Source: ${tableData.metadata.source}`}
            {tableData.metadata.rowCount && ` • ${tableData.metadata.rowCount} rows`}
            {tableData.metadata.metricCount && ` • ${tableData.metadata.metricCount} metrics`}
            {!disablePersistence && ` • Expansion state saved`}
          </div>
        )}
      </div>
      
      {/* Hierarchical table */}
      <div className="overflow-x-auto max-w-full">
        <table className="w-full table-auto border border-gray-300 dark:border-gray-600 text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left border-b border-gray-300 dark:border-gray-600 font-semibold text-xs uppercase tracking-wide">
                Category
              </th>
              {metricColumns.map((column) => (
                <th 
                  key={column}
                  className="px-3 py-2 text-left border-b border-gray-300 dark:border-gray-600 font-semibold text-xs uppercase tracking-wide"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderNode(tableData.data)}
          </tbody>
        </table>
      </div>

      {/* Expand/Collapse All Controls */}
      <div className="mt-4 flex gap-2 text-xs">
        <button
          onClick={expandAll}
          className="px-3 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
        >
          Expand All
        </button>
        <button
          onClick={collapseAll}
          className="px-3 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/90 transition-colors"
        >
          Collapse All
        </button>
      </div>
    </div>
  )
}

export default HierarchicalTable